import giftCardIcon from '@/assets/images/gift-card-icon.png';
import { Button } from '@/components/ui/button';
import { CustomInput, Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { showToast } from '@/components/ui/sonner';
import { AnimatedCounter } from '@/components/ui/animated-counter';
import { useLazyQuery, useMutation } from '@apollo/client';
import { AlertTriangle, ArrowUp, CheckCircle, CheckCircle2, Info, LoaderCircle, Mail, Smartphone, Tag, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  COMPLETE_GIFT_CARD_ACTIVATION,
  CREATE_GIFT_CARD_ACTIVATION_PAYMENT_INTENT,
  CREATE_GIFT_CARD_ACTIVATION_SESSION,
  REDEEM_GIFT_CARD,
  UPDATE_GIFT_CARD_ACTIVATION_DETAILS_SELF,
} from '../Graphql/mutations';
import {
  GET_CURRENT_USER_PHONE,
  PREVIEW_GIFT_CARD_REDEMPTION,
  VALIDATE_GIFT_CARD,
} from '../Graphql/queries';
import GiftCardVisual from './GiftCardVisual';
import type { PackageSummary } from '../index';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Appearance, loadStripe, StripePaymentElementOptions } from '@stripe/stripe-js';

// ─── Stripe setup ─────────────────────────────────────────────────────────────

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

const stripeAppearance: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#F8B107',
    colorBackground: '#181A32',
    colorText: '#FFFFFF',
    colorTextSecondary: '#9CA3AF',
    borderRadius: '8px',
    fontSizeBase: '14px',
  },
};

// ─── Shared types ─────────────────────────────────────────────────────────────

interface DiscountInfo {
  promotionCode: string;
  percentOff?: number | null;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
}

// ─── Inline checkout form (used for pay-to-activate) ──────────────────────────

interface CheckoutFormInlineProps {
  amount: number;
  onBack: () => void;
  onSuccess: () => Promise<void>;
  discount?: DiscountInfo | null;
}

function CheckoutFormInline({ amount, onBack, onSuccess, discount }: CheckoutFormInlineProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [completeActivation] = useMutation(COMPLETE_GIFT_CARD_ACTIVATION);

  const paymentOptions: StripePaymentElementOptions = { layout: 'accordion' };

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsLoading(true);
    try {
      await elements.submit();
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        const msg =
          error.type === 'card_error' || error.type === 'validation_error'
            ? error.message
            : 'An unexpected error occurred.';
        showToast({ variant: 'error', message: msg ?? 'Payment failed.' });
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        const { errors: gqlErrors } = await completeActivation({
          variables: { paymentIntentId: paymentIntent.id },
        });
        if (gqlErrors?.length) throw new Error(gqlErrors[0].message);
        await onSuccess();
      }
    } catch (err: any) {
      showToast({ variant: 'error', message: err?.message ?? 'Something went wrong.' });
    } finally {
      setIsLoading(false);
    }
  };

  const payAmount = discount?.finalAmount ?? amount;

  return (
    <form onSubmit={handlePay} className='flex flex-col gap-5'>
      <PaymentElement options={paymentOptions} />

      {/* Discount breakdown */}
      {discount && (
        <div className='flex flex-col gap-2 rounded-xl bg-[#0E1020] border border-[#2A2D42] p-3'>
          <div className='flex justify-between text-sm'>
            <span className='text-[#9CA3AF]'>Original Price</span>
            <span className='text-white'>${discount.originalAmount.toFixed(2)}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-[#4ADE80]'>
              Discount{discount.percentOff != null ? ` (${discount.percentOff}% off)` : ''}
            </span>
            <span className='text-[#4ADE80]'>-${discount.discountAmount.toFixed(2)}</span>
          </div>
          <div className='h-px bg-white/10' />
          <div className='flex justify-between text-sm font-medium'>
            <span className='text-white'>Final Price</span>
            <span className='text-[#E9CC7C] font-bold'>${discount.finalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}

      <div className='flex gap-3'>
        <Button
          id='activate-pay-back-btn'
          type='button'
          className='flex-1 h-12 font-medium text-white'
          style={{ background: '#343840', borderRadius: '12px' }}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          id='activate-pay-submit-btn'
          type='submit'
          className='flex-1 h-12 font-bold text-black'
          style={{
            background: 'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
            borderRadius: '12px',
          }}
          disabled={isLoading || !stripe || !elements}
        >
          {isLoading ? (
            <LoaderCircle className='h-4 w-4 animate-spin' />
          ) : (
            `Pay $${payAmount.toFixed(2)}`
          )}
        </Button>
      </div>
    </form>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface RedeemGiftCardModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type PreviewData = {
  valid: boolean;
  message: string;
  currentBalance: number;
  sessionsToAdd: number;
  balanceAfterRedemption: number;
  packageSummary: {
    planName: string;
    programName: string;
    sessionCount: number;
    amount: number;
    currency: string;
  };
};

type RedeemResult = {
  /** null when coming from the activate-then-redeem path (balance not fetched to avoid auth flash) */
  balanceAfter: number | null;
  programName: string;
  sessionCount: number;
  packageName: string;
};

type Step = 'code' | 'inactive-info' | 'activate-pay' | 'activate-success' | 'review' | 'success';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns the last 6 chars of the code for masked display */
function maskedCode(code: string) {
  return code.slice(-6);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function RedeemGiftCardModal({
  open,
  onClose,
  onSuccess,
}: RedeemGiftCardModalProps) {
  const [step, setStep] = useState<Step>('code');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  // Redemption (active card) path
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [redeemResult, setRedeemResult] = useState<RedeemResult | null>(null);

  // Pay-to-activate (PENDING_PAYMENT card) path
  const [activationPackage, setActivationPackage] = useState<PackageSummary | null>(null);
  const [, setActivationToken] = useState('');
  const activationTokenRef = useRef('');   // accessible in promo handlers without stale closures
  const [clientSecret, setClientSecret] = useState('');
  const [activateSetupLoading, setActivateSetupLoading] = useState(false);
  const [activateSetupError, setActivateSetupError] = useState('');

  // Promo code state for pay-to-activate
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeError, setPromoCodeError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountInfo | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  // Scroll the modal content back to the top whenever the step changes
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, [step]);

  // ── Queries ────────────────────────────────────────────────────────────────
  const [fetchCurrentUser] = useLazyQuery(GET_CURRENT_USER_PHONE, {
    fetchPolicy: 'network-only',
  });

  const [previewRedemption, { loading: previewing }] = useLazyQuery(
    PREVIEW_GIFT_CARD_REDEMPTION,
    { fetchPolicy: 'network-only' },
  );

  const [validateCard, { loading: validating }] = useLazyQuery(VALIDATE_GIFT_CARD, {
    fetchPolicy: 'network-only',
  });

  // ── Mutations ──────────────────────────────────────────────────────────────
  // Note: do NOT use refetchQueries on this mutation. GetSessionBalance queries getCurrentUser,
  // which momentarily clears the auth cache and unmounts the subscription page — closing the
  // modal before the success screen is visible. We fire the refetch manually after setStep('success').
  const [redeemCard, { loading: redeeming }] = useMutation(REDEEM_GIFT_CARD);
  const [createActivationSession] = useMutation(CREATE_GIFT_CARD_ACTIVATION_SESSION);
  const [updateSelfForActivation] = useMutation(UPDATE_GIFT_CARD_ACTIVATION_DETAILS_SELF);
  const [createActivationIntent] = useMutation(CREATE_GIFT_CARD_ACTIVATION_PAYMENT_INTENT);

  const verifying = previewing || validating;

  // ── Reset / close ──────────────────────────────────────────────────────────
  const handleClose = () => {
    setStep('code');
    setCode('');
    setCodeError('');
    setPreview(null);
    setRedeemResult(null);
    setActivationPackage(null);
    setActivationToken('');
    activationTokenRef.current = '';
    setClientSecret('');
    setActivateSetupLoading(false);
    setActivateSetupError('');
    setPromoCode('');
    setPromoCodeError('');
    setAppliedDiscount(null);
    onClose();
  };

  // ── Step 1: verify code ────────────────────────────────────────────────────
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setCodeError('Please enter a gift card code.');
      return;
    }
    setCodeError('');

    const { data, error } = await previewRedemption({ variables: { code: trimmed } });

    if (error) {
      showToast({ variant: 'error', message: error.message });
      return;
    }

    const result = data?.previewGiftCardRedemption;
    if (!result?.valid) {
      // Secondary check: distinguish an unpaid (PENDING_PAYMENT) card from a truly invalid one
      const { data: validateData } = await validateCard({ variables: { code: trimmed } });
      const validateResult = validateData?.validateGiftCardCodeForActivation;
      if (validateResult?.result === 'PENDING_PAYMENT') {
        setActivationPackage(validateResult.packageSummary as PackageSummary);
        setStep('inactive-info');
      } else {
        setCodeError(result?.message ?? 'Invalid or already redeemed gift card.');
      }
      return;
    }

    setPreview(result as PreviewData);
    setStep('review');
  };

  // ── Pay-to-activate: fetch user, create session, verify, get payment intent ─
  const handleGoToActivatePay = async () => {
    setActivateSetupLoading(true);
    setActivateSetupError('');
    setStep('activate-pay');
    try {
      // Fetch current user to get countryCode + phoneNumber from the backend
      const { data: userData } = await fetchCurrentUser();
      const user = userData?.getCurrentUser;
      if (!user?.phoneNumber || !user?.countryCode) {
        setActivateSetupError(
          'Your account doesn\'t have a phone number on file. Please update your profile and try again.',
        );
        return;
      }

      const cc = String(user.countryCode).replace('+', '');
      const nat = String(user.phoneNumber);

      // Create activation session
      const { data: s } = await createActivationSession({ variables: { code } });
      const tok = s?.createGiftCardActivationSession?.activationSessionToken ?? '';
      setActivationToken(tok);
      activationTokenRef.current = tok;

      // Associate the card with this account
      const { data: selfData, errors: selfErrors } = await updateSelfForActivation({
        variables: {
          activationSessionToken: tok,
          activateForSelf: { countryCode: `+${cc}`, phoneNumber: nat },
        },
      });
      if (selfErrors?.length) throw new Error(selfErrors[0].message);
      if (!selfData?.updateGiftCardActivationDetails?.accountPreview) {
        setActivateSetupError(
          'We couldn\'t verify your account. Please try again or contact support.',
        );
        return;
      }

      // Create Stripe payment intent (no promo on initial load)
      const { data: i, errors: intentErrors } = await createActivationIntent({
        variables: { activationSessionToken: tok },
      });
      if (intentErrors?.length) throw new Error(intentErrors[0].message);

      setClientSecret(
        i?.createGiftCardActivationPaymentIntent?.paymentIntentSecret ?? '',
      );
    } catch (err: any) {
      setActivateSetupError(err?.message ?? 'Setup failed. Please try again.');
    } finally {
      setActivateSetupLoading(false);
    }
  };

  const handleActivatePayBack = () => {
    setPromoCode('');
    setPromoCodeError('');
    setAppliedDiscount(null);
    setStep('inactive-info');
  };

  // ── Promo code handlers (pay-to-activate) ──────────────────────────────────
  // NOTE: We call createActivationIntent directly (no @auth required) instead of
  // first calling validatePromotionCode (which has @auth). The payment intent
  // mutation validates the promo code internally and returns the discount in its
  // response, so the separate validation query is redundant and caused "session
  // expired" errors on mobile when the JWT had rotated mid-flow.
  const handleApplyPromoModal = async () => {
    setPromoCodeError('');
    setIsValidatingPromo(true);
    try {
      const { data: intentData, errors: intentErrors } = await createActivationIntent({
        variables: {
          activationSessionToken: activationTokenRef.current,
          promotionCode: promoCode.trim(),
        },
      });
      if (intentErrors?.length) {
        setPromoCodeError(intentErrors[0].message ?? 'Invalid promo code.');
        return;
      }
      const discount = intentData?.createGiftCardActivationPaymentIntent?.discount;
      if (!discount) {
        setPromoCodeError('Promo code could not be applied.');
        return;
      }
      setAppliedDiscount(discount as DiscountInfo);
      setClientSecret(intentData?.createGiftCardActivationPaymentIntent?.paymentIntentSecret ?? '');
    } catch (err: any) {
      setPromoCodeError(err?.message ?? 'Invalid promo code.');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleRemovePromoModal = async () => {
    setAppliedDiscount(null);
    setPromoCode('');
    setPromoCodeError('');
    try {
      const { data: intentData, errors: intentErrors } = await createActivationIntent({
        variables: { activationSessionToken: activationTokenRef.current },
      });
      if (intentErrors?.length) throw new Error(intentErrors[0].message);
      setClientSecret(intentData?.createGiftCardActivationPaymentIntent?.paymentIntentSecret ?? '');
    } catch (err: any) {
      showToast({ variant: 'error', message: err?.message ?? 'Something went wrong.' });
    }
  };

  // ── Pay-to-activate: after completeActivation succeeds ───────────────────────
  // completeGiftCardActivationAfterPayment (ACTIVATE_FOR_SELF flow) already credits
  // sessions to the account — no separate redeemGiftCard call is needed.
  //
  // We deliberately do NOT query getCurrentUser / GetSessionBalance here.
  // Any getCurrentUser refetch (even via GetSessionBalance) momentarily clears
  // the auth cache, which unmounts the subscription page and closes the modal
  // before the success screen is visible.
  //
  // Instead we build the result directly from activationPackage (balanceAfter = null).
  // The success screen hides the balance block when balanceAfter is null.
  // The home/subscription page refetches balance when the user navigates there.
  const handleActivationSuccess = async () => {
    setRedeemResult({
      balanceAfter: null,
      programName: activationPackage?.programName ?? '',
      sessionCount: activationPackage?.sessionCount ?? 0,
      packageName: activationPackage?.planName ?? '',
    });
    setStep('success');
  };

  // ── activate-success (fallback): sessions already credited, just close ────────
  // completeActivation already added the sessions. The only reason we land here is
  // if the post-activation balance fetch failed. "Continue" closes the modal so the
  // user can refresh/navigate and see their updated balance.
  const handleRedeemNow = () => {
    onSuccess?.();
    handleClose();
  };

  // ── Step review: redeem ────────────────────────────────────────────────────
  const handleRedeem = async () => {
    try {
      const { data, errors } = await redeemCard({
        variables: { code: code.trim().toUpperCase() },
      });
      const result = data?.redeemGiftCard;
      if (result) {
        setRedeemResult(result as RedeemResult);
        setStep('success');
        return;
      }
      if (errors?.length) throw new Error(errors[0].message);
      throw new Error('Redemption returned no data.');
    } catch (err: any) {
      showToast({ variant: 'error', message: err?.message ?? 'Redemption failed.' });
    }
  };

  // ── Title / subtitle per step ──────────────────────────────────────────────
  const hideHeader = step === 'success' || step === 'activate-success';

  const titleMap: Record<Step, string> = {
    code: 'Redeem your Gift Card',
    'inactive-info': 'This Card Isn\'t Active Yet',
    'activate-pay': 'Pay to Activate',
    'activate-success': '',
    review: 'Package Details',
    success: '',
  };

  const subtitleMap: Record<Step, string> = {
    code: '',
    'inactive-info': '',
    'activate-pay': '',
    'activate-success': '',
    review: 'Please review the gift card details before redeeming it to your account.',
    success: '',
  };

  // ── Footer per step ────────────────────────────────────────────────────────
  const footer = (() => {
    if (step === 'code') {
      return (
        <div className='flex items-center gap-3 px-7 py-5'>
          <Button
            id='redeem-cancel-btn'
            className='flex-1 h-12 font-medium text-white'
            style={{ background: '#343840', borderRadius: '12px' }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            id='redeem-verify-btn'
            type='submit'
            form='redeem-code-form'
            className='flex-1 h-12 font-bold text-black'
            style={{
              background: 'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
              borderRadius: '12px',
            }}
            disabled={verifying}
            loading={verifying}
          >
            {verifying ? 'Verifying…' : 'Verify Code'}
          </Button>
        </div>
      );
    }

    if (step === 'inactive-info') {
      return (
        <div className='flex items-center gap-3 px-7 py-5'>
          <Button
            id='inactive-back-btn'
            className='flex-1 h-12 font-medium text-white'
            style={{ background: '#343840', borderRadius: '12px' }}
            onClick={() => setStep('code')}
          >
            Back
          </Button>
          <Button
            id='pay-to-activate-btn'
            className='flex-1 h-12 font-bold text-black'
            style={{
              background: 'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
              borderRadius: '12px',
            }}
            onClick={handleGoToActivatePay}
          >
            Pay to Activate
          </Button>
        </div>
      );
    }

    if (step === 'activate-success') {
      return (
        <div className='px-7 py-5'>
          <Button
            id='activation-continue-btn'
            className='w-full h-12 font-bold text-black'
            style={{
              background: 'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
              borderRadius: '12px',
            }}
            onClick={handleRedeemNow}
          >
            Continue
          </Button>
        </div>
      );
    }

    if (step === 'review') {
      return (
        <div className='flex items-center gap-3 px-7 py-5'>
          <Button
            id='redeem-back-btn'
            className='flex-1 h-12 font-medium text-white'
            style={{ background: '#343840', borderRadius: '12px' }}
            onClick={() => setStep('code')}
          >
            Back
          </Button>
          <Button
            id='redeem-confirm-btn'
            className='flex-1 h-12 font-bold text-black'
            style={{
              background: 'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
              borderRadius: '12px',
            }}
            disabled={redeeming}
            loading={redeeming}
            onClick={handleRedeem}
          >
            {redeeming ? 'Redeeming…' : 'Redeem'}
          </Button>
        </div>
      );
    }

    if (step === 'success') {
      return (
        <div className='px-7 py-5'>
          <Button
            id='redeem-done-btn'
            className='w-full h-12 font-bold text-black'
            style={{
              background: 'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
              borderRadius: '12px',
            }}
            onClick={() => {
              onSuccess?.();
              handleClose();
            }}
          >
            Done
          </Button>
        </div>
      );
    }

    // activate-pay: footer handled by CheckoutFormInline (inside the Elements boundary)
    // or by the Back button in the error state. No top-level footer needed.
    return null;
  })();

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className='bg-[#151525] text-white'
      header={
        !hideHeader ? (
          <div className='flex flex-col gap-1 px-7 pt-7 pb-4'>
            <div className='flex items-start justify-between'>
              <div className='flex flex-col gap-1'>
                <h2 className='text-[26px] font-bold text-white leading-tight'>
                  {titleMap[step]}
                </h2>
                {subtitleMap[step] && (
                  <p className='text-sm font-normal text-white/80'>{subtitleMap[step]}</p>
                )}
              </div>
              <button
                onClick={handleClose}
                className='mt-1 shrink-0 text-white/70 hover:text-white transition-colors'
                aria-label='Close'
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M15 5L5 15M5 5L15 15'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : undefined
      }
      footer={footer}
    >
      {/* Scroll anchor — scrolled into view on every step change */}
      <div ref={scrollAnchorRef} aria-hidden='true' />

      {/* ── Step: Enter Code ──────────────────────────────────────────────── */}
      {step === 'code' && (
        <div className='px-7 py-3 flex flex-col items-center gap-4'>
          {/* Gift card illustration */}
          <img
            src={giftCardIcon}
            alt='Gift Card'
            className='w-24 h-auto select-none'
            draggable={false}
          />

          {/* Code input */}
          <form id='redeem-code-form' onSubmit={handleVerify} className='w-full'>
            <CustomInput
              id='redeem-code-input'
              label='Enter your gift card number*'
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (codeError) setCodeError('');
              }}
              error={codeError}
              className='uppercase'
              autoFocus
            />
          </form>

          {/* Guidance notes */}
          <div className='w-full rounded-xl bg-[#181A32] p-4 flex flex-col gap-3'>
            <p className='text-xs font-semibold text-[#A8ADB9] uppercase tracking-wider'>
              Where to find your code
            </p>
            <div className='flex flex-col gap-3'>
              {/* Gift email */}
              <div className='flex items-start gap-3'>
                <div className='mt-0.5 shrink-0 h-7 w-7 rounded-full bg-[#0A7AFF]/15 flex items-center justify-center'>
                  <Mail className='h-3.5 w-3.5 text-[#0A7AFF]' />
                </div>
                <div>
                  <p className='text-xs font-medium text-white'>Gift Email</p>
                  <p className='text-[11px] text-[#A8ADB9] mt-0.5 leading-relaxed'>
                    Check your inbox for a gift card email — the unique code is printed inside.
                  </p>
                </div>
              </div>
              {/* Physical card */}
              <div className='flex items-start gap-3'>
                <div className='mt-0.5 shrink-0 h-7 w-7 rounded-full bg-[#E9CC7C]/15 flex items-center justify-center'>
                  <Tag className='h-3.5 w-3.5 text-[#E9CC7C]' />
                </div>
                <div>
                  <p className='text-xs font-medium text-white'>Physical Card</p>
                  <p className='text-[11px] text-[#A8ADB9] mt-0.5 leading-relaxed'>
                    The code is printed directly on your physical card.
                  </p>
                </div>
              </div>
              {/* Code format */}
              <div className='flex items-start gap-3'>
                <div className='mt-0.5 shrink-0 h-7 w-7 rounded-full bg-[#22c55e]/10 flex items-center justify-center'>
                  <CheckCircle2 className='h-3.5 w-3.5 text-[#22c55e]' />
                </div>
                <div>
                  <p className='text-xs font-medium text-white'>Code Format</p>
                  <p className='text-[11px] text-[#A8ADB9] mt-0.5 leading-relaxed'>
                    Codes look like{' '}
                    <span className='font-mono text-white/80 tracking-wider'>
                      FPXX-XXXXXXXXXX
                    </span>
                    . You can enter the code with or without spaces — any format works.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Step: Inactive Info ───────────────────────────────────────────── */}
      {step === 'inactive-info' && (
        <div className='px-7 py-5 flex flex-col gap-5'>
          {/* Amber warning callout */}
          <div className='rounded-xl border border-[#F8B107]/30 bg-[#F8B107]/10 p-5 flex flex-col gap-3'>
            <div className='flex items-start gap-3'>
              <AlertTriangle className='h-5 w-5 text-[#F8B107] mt-0.5 shrink-0' />
              <p className='text-sm font-semibold text-white leading-snug'>
                This gift card hasn&apos;t been paid for and is currently inactive.
              </p>
            </div>
            <p className='text-sm text-[#A8ADB9] leading-relaxed'>
              If this card was gifted to you, please check with the sender — they may still
              need to complete the purchase on their end.
            </p>
            <p className='text-sm text-[#A8ADB9] leading-relaxed'>
              If you&apos;d like to pay and activate this card yourself, you can do that right
              here.
            </p>
          </div>

          {/* Package summary (if available) */}
          {activationPackage && (
            <div className='rounded-xl bg-[#181A32] p-4 flex flex-col gap-2'>
              <p className='text-xs font-semibold text-[#A8ADB9] uppercase tracking-wider'>
                Card Details
              </p>
              <div className='flex justify-between'>
                <span className='text-sm text-[#C5C8D0]'>Program</span>
                <span className='text-sm font-medium text-white'>
                  {activationPackage.programName}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-[#C5C8D0]'>Package</span>
                <span className='text-sm font-medium text-white'>
                  {activationPackage.planName}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-[#C5C8D0]'>Sessions</span>
                <span className='text-sm font-medium text-white'>
                  {activationPackage.sessionCount}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-[#C5C8D0]'>Value</span>
                <span className='text-sm font-medium text-white'>
                  ${activationPackage.amount.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step: Pay to Activate ─────────────────────────────────────────── */}
      {step === 'activate-pay' && (
        <div className='px-7 py-5 flex flex-col gap-5'>
          {/* Loading: fetching user + creating session + payment intent */}
          {activateSetupLoading && (
            <div className='flex flex-col items-center gap-4 py-10'>
              <LoaderCircle className='h-8 w-8 animate-spin text-[#F8B107]' />
              <p className='text-sm text-[#A8ADB9]'>Setting up payment…</p>
            </div>
          )}

          {/* Error state */}
          {!activateSetupLoading && activateSetupError && (
            <div className='flex flex-col gap-4 py-4'>
              <div className='rounded-xl border border-red-500/30 bg-red-500/10 p-4 flex items-start gap-3'>
                <Info className='h-4 w-4 text-red-400 mt-0.5 shrink-0' />
                <p className='text-sm text-[#A8ADB9] leading-relaxed'>{activateSetupError}</p>
              </div>
              <Button
                id='activate-retry-btn'
                className='w-full h-11 font-bold text-black'
                style={{
                  background:
                    'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
                }}
                onClick={handleGoToActivatePay}
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Stripe payment form — ready once clientSecret is set */}
          {!activateSetupLoading && !activateSetupError && clientSecret && (
            <>
              {/* Card visual — always shows original (face-value) price */}
              {activationPackage && (
                <div className='flex justify-center'>
                  <div className='w-full max-w-[460px]'>
                    <GiftCardVisual
                      price={`$${(activationPackage.originalPrice ?? activationPackage.amount).toFixed(2)}`}
                      sessionCount={activationPackage.sessionCount}
                    />
                  </div>
                </div>
              )}

              {/* Package summary */}
              {activationPackage && (
                <div className='flex items-center justify-center gap-3 text-sm text-[#A8ADB9]'>
                  <span className='font-medium text-white'>{activationPackage.programName}</span>
                  <span>·</span>
                  <span>{activationPackage.sessionCount} Sessions</span>
                  <span>·</span>
                  <span className='font-medium text-white'>
                    ${(activationPackage.originalPrice ?? activationPackage.amount).toFixed(2)}
                  </span>
                </div>
              )}

              {/* Promo code */}
              <div className='rounded-2xl bg-[#181A32] p-5 flex flex-col gap-4'>
                <p className='text-sm font-semibold text-white'>Have a discount code?</p>
                {appliedDiscount ? (
                  /* Badge only — full breakdown shown in payment section below */
                  <div className='flex items-center justify-between bg-[#1A3D2E] p-3 rounded-xl'>
                    <div className='flex items-center gap-2'>
                      <CheckCircle className='w-5 h-5 text-[#4ADE80]' />
                      <span className='text-sm font-medium text-[#4ADE80]'>
                        {appliedDiscount.promotionCode} applied
                        {appliedDiscount.percentOff != null ? ` (${appliedDiscount.percentOff}% off)` : ''}
                      </span>
                    </div>
                    <button
                      onClick={handleRemovePromoModal}
                      className='text-[#9CA3AF] hover:text-white transition-colors'
                    >
                      <X className='w-4 h-4' />
                    </button>
                  </div>
                ) : (
                  <div className='flex flex-col gap-2'>
                    <div className='flex gap-2'>
                      <Input
                        id='modal-promo-code-input'
                        type='text'
                        placeholder='Enter promo code'
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          if (promoCodeError) setPromoCodeError('');
                        }}
                        autoCorrect='off'
                        autoCapitalize='characters'
                        spellCheck={false}
                        className='flex-1 bg-[#0E1020] border-[#2A2D42] text-white placeholder:text-[#6B7280]'
                      />
                      <button
                        type='button'
                        className='px-5 rounded-xl text-sm font-semibold bg-[#2A2D42] text-white disabled:opacity-50 hover:bg-[#3a3d52] transition-colors'
                        disabled={isValidatingPromo || !promoCode.trim()}
                        onClick={handleApplyPromoModal}
                      >
                        {isValidatingPromo ? <LoaderCircle className='h-4 w-4 animate-spin' /> : 'Apply'}
                      </button>
                    </div>
                    {promoCodeError && <p className='text-[#DC1D00] text-xs'>{promoCodeError}</p>}
                  </div>
                )}
              </div>

              {/* Stripe payment form */}
              <Elements
                key={clientSecret}
                stripe={stripePromise}
                options={{ clientSecret, appearance: stripeAppearance }}
              >
                <CheckoutFormInline
                  amount={activationPackage?.amount ?? 0}
                  onBack={handleActivatePayBack}
                  onSuccess={handleActivationSuccess}
                  discount={appliedDiscount}
                />
              </Elements>
            </>
          )}
        </div>
      )}

      {/* ── Step: Activation Success ──────────────────────────────────────── */}
      {step === 'activate-success' && (
        <div className='px-7 py-8 flex flex-col items-center gap-6'>
          {/* Icon */}
          <div
            className='flex h-[100px] w-[100px] items-center justify-center rounded-full shrink-0'
            style={{ background: '#1C2E1C' }}
          >
            <svg width='50' height='38' viewBox='0 0 50 38' fill='none'>
              <path
                d='M4 19L18 33L46 5'
                stroke='#22c55e'
                strokeWidth='6'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>

          {/* Heading */}
          <div className='flex flex-col items-center gap-2 text-center'>
            <h2 className='text-[28px] font-bold text-white leading-tight'>Card Activated!</h2>
            <p className='text-sm text-[#A8ADB9] leading-relaxed max-w-xs'>
              Your card has been paid for and your sessions have been added to your account.
              Tap <span className='text-white font-medium'>Continue</span> to get started.
            </p>
          </div>

          {/* Package reminder */}
          {activationPackage && (
            <div
              className='w-full rounded-xl p-4 flex flex-col items-center gap-1'
              style={{
                background: 'linear-gradient(135deg, rgba(47,47,47,1) 0%, rgba(47,47,47,1) 100%)',
                boxShadow: 'inset 0 0 0 1px rgba(248,177,7,0.2)',
              }}
            >
              <p className='text-xs font-semibold text-[#A8ADB9] uppercase tracking-wider'>
                Activated Package
              </p>
              <p className='text-base font-semibold text-white mt-1'>
                {activationPackage.programName} — {activationPackage.planName}
              </p>
              <p className='text-sm text-[#A8ADB9]'>
                {activationPackage.sessionCount} Sessions
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Step: Package Review ──────────────────────────────────────────── */}
      {step === 'review' && preview && (
        <div className='px-7 py-5 flex flex-col gap-5'>
          {/* Card visual */}
          <div className='flex justify-center'>
            <div className='w-full max-w-[460px]'>
              <GiftCardVisual
                price={`$${preview.packageSummary.amount.toFixed(2)}`}
                sessionCount={preview.packageSummary.sessionCount}
              />
            </div>
          </div>

          {/* Detail rows */}
          <div
            className='rounded-xl border p-5 flex flex-col gap-4'
            style={{ background: '#181A32', borderColor: '#1780FB' }}
          >
            {/* Gift Card Number */}
            <div className='flex items-center justify-between'>
              <span className='text-base font-normal text-[#C5C8D0]'>Gift Card Number:</span>
              <div className='flex items-center gap-2'>
                <span className='flex items-center gap-1'>
                  {Array.from({ length: 7 }).map((_, i) => (
                    <span
                      key={i}
                      className='inline-block w-2 h-2 rounded-full bg-white/70'
                    />
                  ))}
                </span>
                <span className='text-base font-medium text-white font-mono'>
                  {maskedCode(code)}
                </span>
                {/* Valid pill */}
                <span
                  className='flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-medium'
                  style={{
                    background: 'rgba(95,217,130,0.15)',
                    color: '#5FD982',
                    border: '1px solid #5FD982',
                  }}
                >
                  <CheckCircle2 className='h-3.5 w-3.5' />
                  Valid
                </span>
              </div>
            </div>

            {/* Program */}
            <div className='flex items-center justify-between'>
              <span className='text-base font-normal text-[#C5C8D0]'>Program:</span>
              <span className='text-base font-medium text-white'>
                {preview.packageSummary.programName}
              </span>
            </div>

            {/* Package */}
            <div className='flex items-center justify-between'>
              <span className='text-base font-normal text-[#C5C8D0]'>Package:</span>
              <span className='text-base font-medium text-white'>
                {preview.packageSummary.planName}
              </span>
            </div>

            {/* Gift Card Value */}
            <div className='flex items-center justify-between'>
              <span className='text-base font-normal text-[#C5C8D0]'>Gift Card Value:</span>
              <span className='text-base font-medium text-white'>
                ${preview.packageSummary.amount.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Balance comparison */}
          <div
            className='rounded-xl p-5'
            style={{
              background: 'linear-gradient(135deg, rgba(47,47,47,1) 0%, rgba(47,47,47,1) 100%)',
              boxShadow: 'inset 0 0 0 1px rgba(248,177,7,0.2)',
            }}
          >
            <div className='flex items-center gap-0'>
              {/* Current balance */}
              <div className='flex-1 flex flex-col gap-2 pr-4'>
                <p className='text-base font-medium text-white leading-tight'>
                  Current Available {preview.packageSummary.programName} Balance
                </p>
                <div className='flex items-end gap-2 mt-1'>
                  <span
                    className='text-[52px] font-bold leading-none'
                    style={{
                      background:
                        'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {preview.currentBalance}
                  </span>
                  <span className='text-base font-semibold text-white pb-1'>Sessions</span>
                </div>
              </div>

              {/* Vertical divider */}
              <div className='w-px self-stretch bg-white/20 mx-2' />

              {/* Balance after */}
              <div className='flex-1 flex flex-col gap-2 pl-4'>
                <p className='text-base font-medium text-white leading-tight'>
                  Balance After Redemption
                </p>
                <div className='flex items-end gap-2 mt-1'>
                  <span
                    className='text-[52px] font-bold leading-none'
                    style={{
                      background:
                        'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {preview.balanceAfterRedemption}
                  </span>
                  <span className='text-base font-semibold text-white pb-1'>Sessions</span>
                </div>
              </div>
            </div>
          </div>

          {/* Warning / info note */}
          <div className='flex items-start gap-3'>
            <Info className='h-6 w-6 shrink-0 mt-0.5' style={{ color: '#F8B107' }} />
            <div className='flex flex-col gap-1'>
              <p className='text-base font-medium text-white leading-snug'>
                The gift card will be redeemed to your current FitStatic account.
              </p>
              <p className='text-sm font-medium text-[#A8ADB9] leading-snug'>
                Once redeemed, this gift card cannot be used again.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Step: Redeem Success ──────────────────────────────────────────── */}
      {step === 'success' && redeemResult && (
        <div className='px-7 py-8 flex flex-col items-center gap-6'>
          {/* Icon + heading */}
          <div className='flex flex-col items-center gap-4 text-center'>
            <div
              className='flex h-[100px] w-[100px] items-center justify-center rounded-full shrink-0'
              style={{ background: 'linear-gradient(135deg, #F9CB40 0%, #E9A97C 100%)' }}
            >
              <svg width='50' height='38' viewBox='0 0 50 38' fill='none'>
                <path
                  d='M4 19L18 33L46 5'
                  stroke='white'
                  strokeWidth='6'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <div className='flex flex-col gap-2'>
              <h2 className='text-[32px] font-bold text-white leading-tight'>
                Gift Card Redeemed Successfully
              </h2>
              <p className='text-base text-[#A8ADB9] leading-relaxed max-w-sm mx-auto'>
                Your{' '}
                <span className='text-white font-medium'>
                  {redeemResult.sessionCount} {redeemResult.programName} session
                  {redeemResult.sessionCount !== 1 ? 's' : ''}
                </span>{' '}
                have been credited to your account and are ready to use.
              </p>
            </div>
          </div>

          {/* Updated balance card — only shown when balanceAfter is known (normal redeem path) */}
          {redeemResult.balanceAfter !== null && (
            <div
              className='w-full rounded-xl p-5 flex flex-col items-center gap-1'
              style={{
                background: 'linear-gradient(135deg, rgba(47,47,47,1) 0%, rgba(47,47,47,1) 100%)',
                boxShadow: 'inset 0 0 0 1px rgba(248,177,7,0.25)',
              }}
            >
              <p className='text-xs font-semibold text-[#A8ADB9] uppercase tracking-wider'>
                Updated {redeemResult.programName} Balance
              </p>
              <div className='flex items-end gap-2 mt-1'>
                <AnimatedCounter
                  value={redeemResult.balanceAfter ?? 0}
                  from={(redeemResult.balanceAfter ?? 0) - redeemResult.sessionCount}
                  className='text-[56px] font-bold leading-none'
                  style={{
                    background:
                      'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                />
                <div className='flex flex-col items-start gap-0.5 pb-1.5'>
                  <ArrowUp className='h-4 w-4 text-green-400 shrink-0' />
                  <span className='text-lg font-semibold text-white'>Sessions</span>
                </div>
              </div>
              <p className='text-xs text-[#A8ADB9] mt-1'>
                +{redeemResult.sessionCount} added from this gift card
              </p>
            </div>
          )}

          {/* What to do next */}
          <div className='w-full rounded-xl bg-[#181A32] p-4 flex flex-col gap-3'>
            <p className='text-xs font-semibold text-white uppercase tracking-wider'>
              What to do next
            </p>
            <div className='flex items-center gap-3'>
              <div className='shrink-0 h-7 w-7 rounded-full bg-[#0A7AFF]/15 flex items-center justify-center'>
                <Smartphone className='h-3.5 w-3.5 text-[#0A7AFF]' />
              </div>
              <p className='text-xs text-[#A8ADB9] leading-relaxed'>
                Your sessions are ready — start scheduling your{' '}
                <span className='text-white font-medium'>{redeemResult.programName}</span>{' '}
                sessions.
              </p>
            </div>
            <div className='flex items-center gap-3'>
              <div className='shrink-0 h-7 w-7 rounded-full bg-[#E9CC7C]/15 flex items-center justify-center'>
                <Info className='h-3.5 w-3.5 text-[#E9CC7C]' />
              </div>
              <p className='text-xs text-[#A8ADB9] leading-relaxed'>
                A redemption confirmation has been sent to your registered email address for
                your records.
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
